/** @format */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
	Aggregations,
	Brand,
	CreateQuestionInput,
	Question,
	getPublishBlockers,
	isPreLive,
	isSubjectMatterExpert,
} from "@question-bank/shared";
import { QuestionForm } from "./QuestionForm";
import { getQuestion, updateQuestion } from "../api/questions";
import { useUpdateQuestionStatus } from "../hooks/useUpdateQuestionStatus";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";

interface EditPartModalProps {
	brand: Brand;
	partId: string;
	/** Display number of the part, e.g. "1(a)". */
	partNumber: string;
	/** Display number of the parent root, e.g. "1". */
	parentNumber: string;
	/**
	 * The part's own direct ribbon children (e.g. `1(a)(i)`, `1(a)(ii)` for
	 * `1(a)`). When present, the part is a grouped parent: its marks, content and
	 * mark schemes are derived from these parts rather than required on itself.
	 */
	childParts?: Question[];
	aggregations: Aggregations;
	onClose: () => void;
	onSaved: () => void;
	/** Closes the modal and hands off to the parent's delete confirmation flow. */
	onRequestDelete: () => void;
	/** Notifies the parent to refresh the group after an in-modal status change. */
	onReload?: () => void;
}

export const EditPartModal = ({
	brand,
	partId,
	partNumber,
	parentNumber,
	childParts = [],
	aggregations,
	onClose,
	onSaved,
	onRequestDelete,
	onReload,
}: EditPartModalProps) => {
	const [part, setPart] = useState<Question | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	const {
		isUpdating: isUpdatingStatus,
		error: statusError,
		updateQuestionStatus,
		reset: resetStatusError,
	} = useUpdateQuestionStatus(brand);

	useEffect(() => {
		let isCancelled = false;
		setIsLoading(true);
		setError(null);
		setPart(null);

		getQuestion(brand, partId)
			.then((response) => {
				if (!isCancelled) {
					setPart(response);
				}
			})
			.catch((err) => {
				if (!isCancelled) {
					setError(err instanceof Error ? err.message : "Failed to load part");
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [brand, partId]);

	// Escape to close
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	const formValues = (part ? { ...part } : undefined) as Partial<CreateQuestionInput> | undefined;

	const handleSubmit = async (data: CreateQuestionInput) => {
		await updateQuestion(partId, brand, data);
	};

	const { user } = useAuth();
	const { t } = useTranslation();
	const partStatus = part?.deleted ? "deleted" : (part?.status ?? "published");
	const publishBlockers = part ? getPublishBlockers(part, { parts: childParts }) : [];
	const isBlockPublish = isPreLive(partStatus) && publishBlockers.length > 0;
	const isExpert = isSubjectMatterExpert(user?.group);
	const isStagedWithoutExpert = partStatus === "staged" && !isExpert;
	const isDraftForNonExpert = partStatus === "draft" && !isExpert;

	const handleStatusToggle = async () => {
		if (!part || part.deleted) {
			return;
		}
		const newStatus = partStatus === "published" ? "draft" : isDraftForNonExpert ? "staged" : "published";
		resetStatusError();
		const isSuccess = await updateQuestionStatus(part.id, newStatus);
		if (isSuccess) {
			setPart({ ...part, status: newStatus });
			onReload?.();
		}
	};

	return createPortal(
		<div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
			<div
				ref={modalRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="edit-part-modal-title"
				className="my-4 w-full max-w-4xl rounded-lg border border-slate-200 bg-white shadow-lg"
			>
				<div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
					<div>
						<h2 id="edit-part-modal-title" className="text-base font-semibold text-slate-800">
							Edit part {partNumber}
						</h2>
						<p className="mt-1 text-xs text-slate-500">Part of question {parentNumber}</p>
					</div>
					<div className="flex items-center gap-2">
						{part && partStatus !== "deleted" && (
							<>
								<button
									type="button"
									onClick={() => void handleStatusToggle()}
									disabled={isUpdatingStatus || isStagedWithoutExpert || (isBlockPublish && !isDraftForNonExpert)}
									title={
										isStagedWithoutExpert
											? t("approvalQueue.expertOnly")
											: isBlockPublish
												? publishBlockers.join("; ")
												: undefined
									}
									className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isUpdatingStatus
										? "Updating…"
										: partStatus === "published"
											? "Unpublish"
											: isDraftForNonExpert
												? t("approvalQueue.stage")
												: "Publish"}
								</button>
								<button
									type="button"
									onClick={onRequestDelete}
									className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700"
								>
									Delete
								</button>
							</>
						)}
						<button
							type="button"
							onClick={onClose}
							aria-label="Close"
							className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
						>
							✕
						</button>
					</div>
				</div>

				{statusError && (
					<p role="alert" className="border-b border-red-100 bg-red-50 px-6 py-2 text-sm font-medium text-red-600">
						{statusError}
					</p>
				)}
				{isBlockPublish && (
					<div role="alert" className="border-b border-red-100 bg-red-50 px-6 py-2 text-xs text-red-600">
						<p className="font-semibold">Cannot publish yet:</p>
						<ul className="list-inside list-disc">
							{publishBlockers.map((blocker) => (
								<li key={blocker}>{blocker}</li>
							))}
						</ul>
					</div>
				)}

				<div className="px-2 py-4">
					{isLoading && <p className="px-6 py-8 text-sm text-slate-500">Loading part…</p>}
					{error && (
						<p role="alert" className="px-6 py-8 text-sm font-medium text-red-600">
							{error}
						</p>
					)}
					{!isLoading && !error && formValues && (
						<QuestionForm
							defaultLanguage="en"
							aggregations={aggregations}
							isDisabled={true}
							onSubmit={handleSubmit}
							successMessage="Part updated successfully"
							errorMessage="Failed to update part. Please try again"
							initialValues={formValues}
							hasParts={childParts.length > 0}
							onSuccess={onSaved}
							onCancel={onClose}
						/>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
